import { FormattedMessage, useIntl, defineMessages } from "react-intl";

import Button from "common/core/button";
import WorkflowModal from "common/modals/workflow_modal";
import { DOC_REJECTION_DESCRIPTIONS } from "constants/document";
import { useForm } from "common/core/form";
import { FormRow } from "common/core/form/layout";
import { RadioGroup, RadioInput, RadioLabel } from "common/core/form/option";
import { defaultRequiredMessage, FormattedFieldError, isAriaInvalid } from "common/core/form/error";
import { TextAreaInput } from "common/core/form/text";

export type RejectionFormValues = {
  rejectionReason: string;
  rejectionAnecdote: string;
};
type Props = {
  onSubmitReport: (values: RejectionFormValues) => void | Promise<void>;
  onDismiss: () => void;
};

const MESSAGES = defineMessages({
  placeholder: {
    id: "3a11aeb8-ca68-445b-9e34-946db9ae698b",
    defaultMessage:
      "Add a brief explanation of the issue to help us understand the reason for rejection",
  },
  anecdoteLabel: {
    id: "c0f1b2d3-4e5f-4a6b-8c9d-0e1f2a3b4c5d",
    defaultMessage: "Additional details",
  },
});

export function RejectDocumentModal({ onSubmitReport, onDismiss }: Props) {
  const intl = useIntl();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RejectionFormValues>();
  const submitHandler = handleSubmit(onSubmitReport);

  return (
    <WorkflowModal
      footerSeparator={false}
      title={
        <FormattedMessage
          id="6d209d30-a615-42f9-8f22-d0be4c7db8e7"
          defaultMessage="Reject this document"
        />
      }
      buttons={[
        <Button
          key="0"
          buttonColor="dark"
          variant="tertiary"
          automationId="return-to-meeting-button"
          onClick={onDismiss}
        >
          <FormattedMessage
            id="2070f674-ee5f-4489-8275-4c1c54ec4114"
            defaultMessage="Return to meeting"
          />
        </Button>,
        <Button
          key="1"
          buttonColor="action"
          variant="primary"
          automationId="submit-report-button"
          onClick={submitHandler}
          isLoading={isSubmitting}
        >
          <FormattedMessage
            id="98427ad3-2666-4e4e-99da-64e293dd7b43"
            defaultMessage="Submit report"
          />
        </Button>,
      ]}
    >
      <form onSubmit={submitHandler}>
        <FormRow>
          <RadioGroup
            displayRequiredAsterisk
            groupError={
              <FormattedFieldError inputName="rejectionReason" error={errors.rejectionReason} />
            }
            label={
              <FormattedMessage
                id="42232106-2693-4c8d-9cff-c75f67ce2853"
                defaultMessage="Why did you reject this document?"
              />
            }
          >
            {Object.entries(DOC_REJECTION_DESCRIPTIONS).map(([key, val]) => (
              <RadioLabel
                label={intl.formatMessage(val)}
                key={key}
                radio={
                  <RadioInput
                    value={key}
                    error={errors.rejectionReason}
                    {...register("rejectionReason", {
                      required: defaultRequiredMessage(intl),
                    })}
                  />
                }
              />
            ))}
          </RadioGroup>
        </FormRow>
        <FormRow>
          <TextAreaInput
            label={intl.formatMessage(MESSAGES.anecdoteLabel)}
            placeholder={intl.formatMessage(MESSAGES.placeholder)}
            aria-invalid={isAriaInvalid(errors.rejectionAnecdote)}
            {...register("rejectionAnecdote", {
              required: false,
            })}
          />
          <FormattedFieldError inputName="rejectionReason" error={errors.rejectionAnecdote} />
        </FormRow>
      </form>
    </WorkflowModal>
  );
}
